// OP-3605 — Equipamentos (ESL1): as peças de um item numa praça, com os quatro
// eixos. A lista é a porta; quem decide sobre a peça é a tela dela.
import { FC, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BUSCA_MINIMA_EQUIPAMENTOS, useEstoqueEquipamentos, useEstoqueItensSolicitacao, useEstoqueLocais } from '../../../../hooks/estoque'
import { getEstoqueUnidadePorCodigo } from '../../../../Service/estoque'
import { EquipamentoRow, ItemDoFiltro, PracaDoFiltro, UnidadePorCodigo, UnidadesPorCodigo } from '../../../../types/estoque'
import {
  BD, SEVERIDADE, Button, CaixaLista, Card, Field, FiltersRow, ListaCartoes, PageSubtitle, PageTitle,
  Select, Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { CampoComLeitor } from '../CampoComLeitor'
import { Cartao } from '../Cartao'
import { tratarErro } from '../erros'
import { CICLO_UI, CONDICAO_UI, SITUACAO_UI, baseNaLista, leituraDaPeca } from '../Equipamento/regras'
import { TODAS, TODOS, filtrosIniciais, lerItem, lerPraca, podeVerTodasAsPracas, tituloDaPeca } from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const CASOU_EM: Record<UnidadePorCodigo['casouEm'], string> = {
  SERIAL: 'pelo serial',
  PATRIMONIO: 'pelo patrimônio',
  FABRICANTE: 'pela série do fabricante',
}
const FILTROS_KEY = 'estoque.equipamentos.filtros'

export const EstoqueEquipamentos: FC = () => {
  const navigate = useNavigate()
  const { locais, isLoading: carregandoLocais, isError: erroLocais, isUnauthorized, mutate: recarregarLocais } = useEstoqueLocais()
  const itensQ = useEstoqueItensSolicitacao()
  const itens = itensQ.data
  const [localId, setLocalId] = useState<PracaDoFiltro | null>(null)
  const [itemId, setItemId] = useState<ItemDoFiltro>(TODOS)
  const [busca, setBusca] = useState('')
  const [page, setPage] = useState(1)
  const [lidasPorCodigo, setLidasPorCodigo] = useState<UnidadesPorCodigo | null>(null)
  const [procurando, setProcurando] = useState<string | null>(null)
  const consultaCodigo = useRef(0)
  const itensDePeca = itens.filter((i) => i.controle === 'SERIAL')
  const itemForaDoCatalogo = itensQ.isError || (itens.length > 0 && !itensDePeca.some((i) => i.id === itemId))
  const itemDoFiltro: ItemDoFiltro = itemId !== TODOS && itemForaDoCatalogo ? TODOS : itemId
  const { rows, total, lastPage, isLoading, isError, mutate, termo } = useEstoqueEquipamentos({ localId, itemId: itemDoFiltro, busca, page })
  const verTodas = podeVerTodasAsPracas(locais)

  useEffect(() => {
    if (localId != null || !locais.length) return
    let salvo: { localId?: unknown; itemId?: unknown } | null = null
    try { salvo = JSON.parse(sessionStorage.getItem(FILTROS_KEY) || 'null') } catch { /* ignora */ }
    const inicial = filtrosIniciais(salvo, locais)
    setLocalId(inicial.localId)
    setItemId(inicial.itemId)
  }, [locais, localId])

  useEffect(() => {
    if (localId == null) return
    try { sessionStorage.setItem(FILTROS_KEY, JSON.stringify({ localId, itemId: itemDoFiltro })) } catch { /* ignora */ }
  }, [localId, itemDoFiltro])


  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const umItem = itemDoFiltro !== TODOS
  const cols = umItem ? 6 : 7
  const localDaLista = localId === 'todas' ? null : localId

  const cartao = (u: EquipamentoRow) => {
    const leitura = leituraDaPeca(u as any)
    const base = baseNaLista(u, localDaLista)
    const foraDoPatrimonio = u.cicloVida === 'EXTRAVIADO' || u.cicloVida === 'BAIXADO'
    return (
      <Cartao
        key={u.id}
        to={`/interno/estoque/equipamentos/${u.id}`}
        codigo={u.serial}
        badge={leitura.chips[0]}
        titulo={tituloDaPeca(u)}
        destaque={foraDoPatrimonio ? 'critico' : false}
        esmaecido={u.cicloVida === 'BAIXADO'}
        meta={(
          <>
            pat. {u.patrimonio}
            {leitura.chips.length > 1 ? ` · ${leitura.chips.slice(1).map((c) => c.label).join(' · ')}` : ''}
            {u.cicloVida === 'ATIVO' ? ` · ${CICLO_UI.ATIVO.label}` : ''}
            {leitura.contexto ? <div>{leitura.contexto}</div> : null}
          </>
        )}
        rodape={leitura.contexto
          ? <span style={{ color: SEVERIDADE.critico.texto, fontWeight: 600 }}>Fora do patrimônio regular</span>
          : (
            <span style={{ color: BD.grayText }}>
              {u.localNome}
              {base ? ` · ${base}` : ''}
            </span>
          )}
        rodapeFim={Number(u.riscoNaoRetorno) > 0 ? 'risco de não retorno' : undefined}
      />
    )
  }

  const linha = (u: EquipamentoRow) => {
    const s = SITUACAO_UI[u.situacao] ?? { bg: '#F3F4F6', color: '#374151', label: u.situacao }
    const c = CONDICAO_UI[u.condicao] ?? { bg: '#F3F4F6', color: '#374151', label: u.condicao }
    const cv = CICLO_UI[u.cicloVida] ?? { bg: '#F3F4F6', color: '#374151', label: u.cicloVida }
    const base = baseNaLista(u, localDaLista)
    return (
      <tr key={u.id} onClick={() => navigate(`/interno/estoque/equipamentos/${u.id}`)} style={{ cursor: 'pointer' }}>
        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{u.serial}</td>
        {!umItem && <td style={{ fontSize: 13 }}>{tituloDaPeca(u)}</td>}
        <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.patrimonio}</td>
        <td><StatusBadge $bg={s.bg} $color={s.color}>{s.label}</StatusBadge></td>
        <td><StatusBadge $bg={c.bg} $color={c.color}>{c.label}</StatusBadge></td>
        <td><StatusBadge $bg={cv.bg} $color={cv.color}>{cv.label}</StatusBadge></td>
        <td style={{ fontSize: 13 }}>
          {u.localNome}
          {base && <div style={{ fontSize: 11, color: BD.grayText }}>{base}</div>}
          {Number(u.riscoNaoRetorno) > 0 && <div style={{ fontSize: 11, color: BD.red }}>risco de não retorno</div>}
        </td>
      </tr>
    )
  }

  const abrirPeloCodigo = async (codigo: string) => {
    const esta = ++consultaCodigo.current
    setBusca(codigo)
    setPage(1)
    setLidasPorCodigo(null)
    setProcurando(codigo)
    try {
      const { data } = await getEstoqueUnidadePorCodigo(codigo)
      if (esta !== consultaCodigo.current) return
      const r: UnidadesPorCodigo = data?.data ?? { pecas: [], cortou: false }
      if (r.pecas.length === 1 && !r.cortou) navigate(`/interno/estoque/equipamentos/${r.pecas[0].id}`)
      else if (r.pecas.length === 0) toast.info(`Nenhuma peça com o código ${codigo} nas praças que você opera.`)
      else setLidasPorCodigo(r)
    } catch (err) {
      if (esta !== consultaCodigo.current) return
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
    } finally {
      if (esta === consultaCodigo.current) setProcurando(null)
    }
  }

  return (
    <>
      <PageTitle>Equipamentos</PageTitle>
      <PageSubtitle>
        As peças de patrimônio por praça e item, incluindo as que estão fora mas têm base na praça. Cada peça tem quatro eixos: o que está acontecendo, onde está, se funciona e se ainda é patrimônio nosso.
      </PageSubtitle>

      <FiltersRow>
        <Field>
          <label>Praça</label>
          <Select value={localId ?? ''} onChange={(e) => { setLocalId(lerPraca(e.target.value)); setPage(1) }}>
            {verTodas && <option value={TODAS}>Todas</option>}
            {locais.map((l) => <option key={l.id} value={l.id} disabled={!l.noEscopo}>{l.nome}{l.noEscopo ? '' : ' (fora do seu escopo)'}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Item</label>
          <Select value={itemDoFiltro} onChange={(e) => { setItemId(lerItem(e.target.value)); setPage(1) }}>
            <option value={TODOS}>Todos</option>
            {itensDePeca.map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Serial, patrimônio ou série do fabricante</label>
          <CampoComLeitor
            valor={busca}
            aoMudar={(v) => { consultaCodigo.current++; setProcurando(null); setBusca(v); setPage(1); setLidasPorCodigo(null) }}
            aoLer={abrirPeloCodigo}
            tituloLeitor="Ler a etiqueta da peça"
            placeholder={`mínimo ${BUSCA_MINIMA_EQUIPAMENTOS} caracteres`}
            rotulo="Serial, patrimônio ou série do fabricante"
          />
        </Field>
      </FiltersRow>

      {procurando && <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>Procurando a peça {procurando}…</div>}
      {!procurando && busca.trim().length > 0 && busca.trim().length < BUSCA_MINIMA_EQUIPAMENTOS && (
        <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>Digite pelo menos {BUSCA_MINIMA_EQUIPAMENTOS} caracteres para buscar.</div>
      )}

      {lidasPorCodigo && (
        <Card style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Mais de uma peça com o código {busca}</div>
          {lidasPorCodigo.pecas.map((u) => (
            <div key={u.id} style={{ padding: '6px 0', fontSize: 14 }}>
              <a href={`/interno/estoque/equipamentos/${u.id}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/equipamentos/${u.id}`) }}>{u.serial}</a>
              <span style={{ color: BD.grayText }}> · {CASOU_EM[u.casouEm]} · {u.itemNome} · {u.localNome}</span>
            </div>
          ))}
          {lidasPorCodigo.cortou && (
            <div style={{ marginTop: 6, fontSize: 12, color: BD.grayText }}>Há mais peças com este código do que as mostradas: provavelmente a série do fabricante está repetida no cadastro.</div>
          )}
        </Card>
      )}

      {carregandoLocais ? <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
        : erroLocais ? (
          <Card><div style={{ padding: 24, color: BD.red }}>
            Não foi possível carregar as praças. <Button $variant="secondary" onClick={() => recarregarLocais()}>Tentar de novo</Button>
          </div></Card>
        ) : !locais.some((l) => l.noEscopo) ? (
          <Card><div style={{ padding: 24, color: BD.grayText }}>Você não opera nenhuma praça: não há equipamento a listar.</div></Card>
        ) : (
          <CaixaLista>
            {itensQ.isError && (
              <div style={{ padding: '8px 12px', fontSize: 13, color: BD.red }}>
                O catálogo de itens não carregou: o filtro de item fica só em "Todos".{' '}
                <Button $variant="secondary" onClick={() => itensQ.mutate()}>Tentar de novo</Button>
              </div>
            )}
            <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>
              {isLoading ? 'Carregando…' : `${total} peça${total === 1 ? '' : 's'}${termo ? ` com "${termo}"` : ''}`}
            </div>
            <TabelaWrap>
              <Table>
                <thead><tr><th>Serial</th>{!umItem && <th>Item</th>}<th>Patrimônio</th><th>Situação</th><th>Condição</th><th>Ciclo de vida</th><th>Onde está</th></tr></thead>
                <tbody>
                  {isLoading && <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
                  {!isLoading && isError && (
                    <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                      Não foi possível carregar as peças. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                    </td></tr>
                  )}
                  {!isLoading && !isError && rows.length === 0 && (
                    <tr><td colSpan={cols} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>
                      {termo ? 'Nenhuma peça com essa busca.' : 'Nenhuma peça com esses filtros.'}
                    </td></tr>
                  )}
                  {!isLoading && !isError && rows.map(linha)}
                </tbody>
              </Table>
            </TabelaWrap>

            <ListaCartoes>
              {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
              {!isLoading && isError && (
                <Card style={{ padding: 20, color: BD.red }}>
                  Não foi possível carregar as peças. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                </Card>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <Card style={{ padding: 20, color: BD.grayText }}>
                  {termo ? 'Nenhuma peça com essa busca.' : 'Nenhuma peça com esses filtros.'}
                </Card>
              )}
              {!isLoading && !isError && rows.map(cartao)}
            </ListaCartoes>
            {(page > 1 || !lastPage) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
                <Button $variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Anterior</Button>
                <span style={{ fontSize: 13, color: BD.grayText }}>Página {page}</span>
                <Button $variant="secondary" disabled={!!isError || isLoading || !!lastPage} onClick={() => setPage(page + 1)}>Próxima ›</Button>
              </div>
            )}
          </CaixaLista>
        )}
    </>
  )
}

export default EstoqueEquipamentos
